import { isText } from '../../../helpers/dom/node';
import { normalizeTableZone } from '../../../helpers/dom/normalize-table-zone';
import {
  appendNewCellToRow,
  appendNewRowToTable,
  getCellCoordinateInPlan,
  getTablePlan,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  isTableSectionElement,
  makeTableRectangle,
  TablePlan,
} from '../../../helpers/dom/table';

/**
 * Replace content of table by tableToPaste's cells, starting from refCell and ending wherever it's needed to copy
 * them all (rows and columns will be created if necessary).
 */
export function pasteCells(
  table: HTMLTableElement,
  refCell: HTMLTableCellElement,
  tableToPaste: HTMLTableElement,
) {
  const document = table.ownerDocument;
  if (!document) {
    return;
  }
  const tablePlanToPaste = getTablePlan(tableToPaste, true);
  const startingCoordinate = getCellCoordinateInPlan(refCell, getTablePlan(table));
  if (startingCoordinate === null) {
    return;
  }
  // Breaks spreading cells within the table zone where data's content will be pasted into 1*1 cells.
  normalizeTableZone(
    table,
    startingCoordinate.row,
    startingCoordinate.row + tablePlanToPaste.length - 1,
    startingCoordinate.column,
    startingCoordinate.column + Math.max(...tablePlanToPaste.map(row => row.length)) - 1,
  );
  const tablePlan = getTablePlan(table, true);
  const recycledCells = new Set<HTMLTableCellElement>(); // Set of cells existing in table, whose content got changed while pasting.
  const pastedCells = new Set<HTMLTableCellElement>(); // Set of cells that got pasted.

  // Iterating through the rows to paste.
  let rowIndex = startingCoordinate.row;
  tablePlanToPaste.forEach(rowPlanToPaste => {
    let cellIndex = startingCoordinate.column;
    // Let's first try to find a non empty row to recycle (non empty because they can be used as separator).
    let recyclableRowPlan: HTMLTableCellElement[];
    [recyclableRowPlan, rowIndex] = getFirstNonEmptyRowPlan(tablePlan, rowIndex);
    // row is the DOM element in which newly created cells will be appended.
    const row = recyclableRowPlan ? table.rows[rowIndex] : appendNewRowToTable(table, cellIndex, document);

    // Iterating through the cells to paste.
    rowPlanToPaste.forEach(cellToPaste => {
      // Let's first try to find a cell to recycle.
      let recyclableCell: HTMLTableCellElement | undefined;
      [recyclableCell, cellIndex] = getFirstRecyclableCell(recyclableRowPlan, row, cellIndex, recycledCells);
      // If cellToPaste has rowspan or col span greater than one, maybe it got already pasted.
      if (pastedCells.has(cellToPaste)) {
        // If there was a recyclableCell left, remove it, otherwise, let's just not add one more.
        if (recyclableCell) {
          recyclableCell.remove();
        }
        return;
      }
      // Either we paste cellToPaste in recyclableCell, or in a new cell if no cell could be recycled.
      const cellToPasteIn = recyclableCell || appendNewCellToRow(table, row, document);
      pasteCell(cellToPaste, cellToPasteIn);
      if (recyclableCell !== undefined) {
        recycledCells.add(recyclableCell);
      }
      cellIndex += 1;
      pastedCells.add(cellToPaste);
    });
    rowIndex += 1;
  });
  makeTableRectangle(table);
}

/**
 * Creates a table out of htmlToPaste if it contains only table elements (td, th, tr, tbody, thead, tfoot, table)
 * and returns it.
 */
export function getTableToPaste(htmlToPaste: string, document: Document) {
  const container = document.createElement('template');
  container.innerHTML = htmlToPaste;
  const { content } = container;
  // Remove meta tag (appended by default in clipboard).
  Array.from(content.children)
    .filter(child => child.tagName === 'META')
    .forEach(meta => meta.remove());

  // Do nothing if clipboard's root contains no element.
  if (content.children.length === 0) {
    return null;
  }

  // Do nothing if clipboard's root contains non empty text node.
  const containsNonEmptyTextNode =
    Array.from(content.childNodes)
      .filter(node => isText(node))
      .map(textNode => (textNode.textContent ? textNode.textContent.trim().length : 0))
      .reduce((acc, length) => acc + length, 0) > 0;
  if (containsNonEmptyTextNode) {
    return null;
  }

  let childrenArr = Array.from(content.children);
  // If all children are td, wrap them in a tr.
  if (childrenArr.filter(isTableCellElement).length === content.children.length) {
    const tr = document.createElement('tr');
    tr.append(...childrenArr);
    container.innerHTML = tr.outerHTML;
    childrenArr = [tr];
  }
  // If all children are tr, wrap them in a tbody.
  if (childrenArr.filter(isTableRowElement).length === content.children.length) {
    const tbody = document.createElement('tbody');
    tbody.append(...childrenArr);
    container.innerHTML = tbody.outerHTML;
    childrenArr = [tbody];
  }
  // If all children are tableSection, wrap them in a table.
  if (childrenArr.filter(isTableSectionElement).length === content.children.length) {
    const table = document.createElement('table');
    table.append(...childrenArr);
    container.innerHTML = table.outerHTML;
    childrenArr = [table];
  }
  // If only one child left, and it's a table, return its tablePlan.
  if (childrenArr.length === 1 && isTableElement(content.firstElementChild)) {
    return content.firstElementChild;
  }
  return null;
}

/**
 * Looks for the first non empty row in tablePlan starting from startIndex.
 * Returns this row and its index in tablePlan.
 */
function getFirstNonEmptyRowPlan(tablePlan: TablePlan, startIndex: number): [HTMLTableCellElement[], number] {
  let index = startIndex;
  let rowPlan = tablePlan[startIndex];
  // Let's not alter empty rows as the can be used as separators.
  while (rowPlan !== undefined && rowPlan.length === 0) {
    index += 1;
    rowPlan = tablePlan[index];
  }
  return [rowPlan, index];
}

/**
 * Looks for the first cell in rowPlan, starting from cellIndex, that belongs to this row and that is not part of recycledCells.
 * Returns this cell and its index in rowPlan.
 * @param rowPlan: rowPlan in which we look for a cell to recycle.
 * @param row: HTMLTableRowElement represented by rowPlan.
 * @param cellIndex: position in rowPlan at which we start searching.
 * @param recycledCells: set of cells that were already recycled.
 */
function getFirstRecyclableCell(
  rowPlan: HTMLTableCellElement[],
  row: HTMLTableRowElement,
  cellIndex: number,
  recycledCells: Set<HTMLTableCellElement>,
): [HTMLTableCellElement | undefined, number] {
  if (!rowPlan) {
    return [undefined, cellIndex];
  }
  let newCellIndex = cellIndex;
  let maybeRecyclableCell: HTMLTableCellElement | undefined = rowPlan[newCellIndex];

  while (maybeRecyclableCell !== undefined) {
    // If cell has a colspan greater than one, it could already have been recycled.
    const isCellAlreadyRecycled = recycledCells.has(maybeRecyclableCell);
    // If cell has a rowspan greater than one, it could belong to a previous row.
    const isCellInAnotherRow = maybeRecyclableCell.parentElement !== row;
    // Let's try to find a cell that is in none of those cases.
    if (!isCellAlreadyRecycled && !isCellInAnotherRow) {
      break;
    }
    newCellIndex += 1;
    maybeRecyclableCell = rowPlan[newCellIndex];
  }
  return [maybeRecyclableCell, newCellIndex];
}

/**
 * Replaces content of cellToPasteIn by content of cellToPaste.
 * Changes dimensions of cellToPasteIn in favour of cellToPaste's ones.
 * Provides cellToPaste style to cellToPasteIn.
 */
function pasteCell(cellToPaste: HTMLTableCellElement, cellToPasteIn: HTMLTableCellElement) {
  // eslint-disable-next-line no-param-reassign
  cellToPasteIn.innerHTML = cellToPaste.innerHTML;
  // Pasted cells keep their original dimensions.
  cellToPasteIn.removeAttribute('rowspan');
  cellToPasteIn.removeAttribute('colspan');
  const { rowSpan, colSpan } = cellToPaste;
  if (rowSpan > 1) {
    cellToPasteIn.setAttribute('rowspan', rowSpan.toString());
  }
  if (colSpan > 1) {
    cellToPasteIn.setAttribute('colspan', colSpan.toString());
  }
  // Provides cellToPaste style to cellToPasteIn if any.
  const cellToPasteStyle = cellToPaste.getAttribute('style');
  if (cellToPasteStyle !== null && cellToPasteStyle.trim().length > 0) {
    cellToPasteIn.setAttribute('style', cellToPasteStyle);
  }
}
